/**
 * Geometria do tabuleiro: grade de 10 unidades (cantos de 1,5 e 7 casas de 1 por lado)
 * e a projeção da mesa inclinada para a tela.
 *
 * Em tela larga a mesa fica em losango, como no Business Tour (giro de 45° e inclinação de 60°).
 * No celular em pé ela fica reta, só um pouco inclinada, para as casas ficarem maiores.
 */
import { BOARD_SIZE } from '@tigrinho/engine';

export type Side = 'bottom' | 'left' | 'top' | 'right' | 'corner';

/** Casas por lado entre os cantos. */
const PER_SIDE = BOARD_SIZE / 4 - 1; // 7
/** Tamanho da grade em unidades (cantos de 1,5). */
export const GRID_UNITS = PER_SIDE + 3; // 10

/**
 * Linha e coluna (1 a 9) de cada casa na grade. O Início fica no canto de baixo à direita
 * e o caminho segue para a esquerda (no losango: de baixo para a esquerda, como no BT).
 */
export function cellPos(i: number): { row: number; col: number; side: Side } {
  const n = PER_SIDE + 2; // 9 trilhas
  const q = PER_SIDE + 1; // 8 casas por lado contando o canto
  if (i === 0) return { row: n, col: n, side: 'corner' };
  if (i < q) return { row: n, col: n - i, side: 'bottom' };
  if (i === q) return { row: n, col: 1, side: 'corner' };
  if (i < 2 * q) return { row: n - (i - q), col: 1, side: 'left' };
  if (i === 2 * q) return { row: 1, col: 1, side: 'corner' };
  if (i < 3 * q) return { row: 1, col: 1 + (i - 2 * q), side: 'top' };
  if (i === 3 * q) return { row: 1, col: n, side: 'corner' };
  return { row: 1 + (i - 3 * q), col: n, side: 'right' };
}

/** Início de cada trilha em unidades (a primeira e a última medem 1,5). */
function trackStart(k: number): number {
  return k === 1 ? 0 : 1.5 + (k - 2);
}
function trackSize(k: number): number {
  return k === 1 || k === PER_SIDE + 2 ? 1.5 : 1;
}

/** Centro da casa em unidades da grade (0 a 10). */
export function cellCenterUnits(i: number): { x: number; y: number } {
  const { row, col } = cellPos(i);
  return { x: trackStart(col) + trackSize(col) / 2, y: trackStart(row) + trackSize(row) / 2 };
}

/** Ponto da casa um pouco para fora (onde fica o prédio) ou para dentro (onde fica o preço). */
export function cellPoint(i: number, outward: number): { x: number; y: number } {
  const c = cellCenterUnits(i);
  const { side } = cellPos(i);
  switch (side) {
    case 'bottom':
      return { x: c.x, y: c.y + outward };
    case 'top':
      return { x: c.x, y: c.y - outward };
    case 'left':
      return { x: c.x - outward, y: c.y };
    case 'right':
      return { x: c.x + outward, y: c.y };
    default:
      return c;
  }
}

export interface View {
  mode: 'iso' | 'flat';
  /** Giro da mesa (graus). */
  spin: number;
  /** Inclinação (graus): 0 = vista de cima. */
  tilt: number;
}

export const ISO: View = { mode: 'iso', spin: 45, tilt: 60 };
export const FLAT: View = { mode: 'flat', spin: 0, tilt: 26 };

export interface Layout {
  view: View;
  /** Largura e altura da mesa em px (no losango são iguais; no celular em pé a mesa fica mais alta). */
  w: number;
  h: number;
  /** Uma unidade da grade na horizontal e na vertical, em px. */
  ux: number;
  uy: number;
  /** Unidade para o tamanho de peões, prédios e letras (média das duas). */
  unit: number;
  /** Centro da mesa no palco. */
  cx: number;
  cy: number;
}

const rad = (d: number) => (d * Math.PI) / 180;

/** Escolhe a vista e o tamanho da mesa que cabem no palco. */
export function fitBoard(width: number, height: number, margin = 8): Layout {
  const view = width / Math.max(1, height) >= 1.15 ? ISO : FLAT;
  const cos = Math.cos(rad(view.tilt));
  if (view.mode === 'iso') {
    const c = Math.abs(Math.cos(rad(view.spin)));
    const s = Math.abs(Math.sin(rad(view.spin)));
    const slab = 0.035;
    const size = Math.max(120, Math.min((width - margin * 2) / (c + s), (height - margin * 2) / ((c + s) * cos + slab)));
    const u = size / GRID_UNITS;
    return { view, w: size, h: size, ux: u, uy: u, unit: u, cx: width / 2, cy: height / 2 - (size * slab) / 2 };
  }
  // Em pé: a mesa usa a largura toda e cresce para baixo (até 1,5 vez a largura).
  const w = Math.max(120, width - margin * 2);
  const slab = 0.02 * w;
  const h = Math.max(w * 0.9, Math.min((height - margin * 2 - slab) / cos, w * 1.5));
  const ux = w / GRID_UNITS;
  const uy = h / GRID_UNITS;
  return { view, w, h, ux, uy, unit: Math.sqrt(ux * uy), cx: width / 2, cy: height / 2 - slab / 2 };
}

/** Projeta um ponto da mesa (em unidades da grade) para a tela (px no palco). */
export function project(l: Layout, x: number, y: number): { x: number; y: number } {
  const u = (x / GRID_UNITS - 0.5) * l.w;
  const v = (y / GRID_UNITS - 0.5) * l.h;
  const sp = rad(l.view.spin);
  const x1 = u * Math.cos(sp) - v * Math.sin(sp);
  const y1 = u * Math.sin(sp) + v * Math.cos(sp);
  return { x: l.cx + x1, y: l.cy + y1 * Math.cos(rad(l.view.tilt)) };
}
